import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { CheckCircle2, XCircle } from "lucide-react";

export type ToastType = "default" | "success" | "error";

/**
 * Botão opcional no aviso (ex.: "Desfazer"). Tocar roda `onPress` e fecha o
 * aviso na hora. Sem ação, o toast continua só texto, como sempre foi.
 */
export type ToastAction = { label: string; onPress: () => void };

type ToastOptions = { type?: ToastType; action?: ToastAction };

type ToastCtx = {
  showToast: (message: string, options?: ToastOptions) => void;
};

type ToastItem = { message: string; type: ToastType; action?: ToastAction };

type ToastState = {
  message: string;
  type: ToastType;
  action?: ToastAction;
  exiting: boolean;
} | null;

/** Tempo na tela + tempo da animação de saída — exportados para o teste da fila não repetir os números soltos. */
export const TOAST_DISPLAY_MS = 3500;
export const TOAST_EXIT_MS = 250;

const ToastContext = createContext<ToastCtx | null>(null);

/**
 * Fila FIFO, não last-wins.
 *
 * Antes, um segundo `showToast` enquanto o primeiro ainda estava na tela
 * sobrescrevia o toast atual — o primeiro aviso nunca chegava a ser lido.
 * Com onboarding, foco, streak recovery e mais de 30 call sites disparando
 * toast, dois avisos em sequência rápida (ex.: salvar um campo logo após
 * outro) são o caso comum, não o raro.
 *
 * A fila vive em ref (não é estado — não precisa re-render por si só) e
 * `busyRef` marca "tem toast mostrando ou saindo agora", para `showToast`
 * saber se deve tocar a fila imediatamente ou só enfileirar.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState>(null);
  const queueRef = useRef<ToastItem[]>([]);
  const busyRef = useRef(false);
  const dismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (dismissRef.current) clearTimeout(dismissRef.current);
    if (exitRef.current) clearTimeout(exitRef.current);
    dismissRef.current = null;
    exitRef.current = null;
  }, []);

  // Ref e não chamada direta: `playNext` reagenda A SI MESMO no fim do ciclo
  // (via `exitRef`), e referenciar o `useCallback` dentro do próprio corpo
  // antes de ele terminar de ser declarado é o que o lint de imutabilidade
  // dos hooks recusa.
  const playNextRef = useRef<() => void>(() => {});

  const playNext = useCallback(() => {
    const next = queueRef.current.shift();
    if (!next) {
      busyRef.current = false;
      setToast(null);
      return;
    }
    busyRef.current = true;
    setToast({ ...next, exiting: false });
    dismissRef.current = setTimeout(() => {
      setToast((prev) => (prev ? { ...prev, exiting: true } : null));
      exitRef.current = setTimeout(() => playNextRef.current(), TOAST_EXIT_MS);
    }, TOAST_DISPLAY_MS);
  }, []);

  useEffect(() => {
    playNextRef.current = playNext;
  }, [playNext]);

  const showToast = useCallback(
    (message: string, options?: ToastOptions) => {
      queueRef.current.push({ message, type: options?.type ?? "default", action: options?.action });
      // Já tem um rodando (mostrando ou saindo)? Só entra na fila — quem está
      // na tela termina o próprio ciclo antes de ceder lugar.
      if (busyRef.current) return;
      playNext();
    },
    [playNext],
  );

  /** O botão do aviso: roda a ação e fecha já, sem esperar os 3,5 s. */
  const pressAction = useCallback(
    (action: ToastAction) => {
      action.onPress();
      clearTimers();
      setToast((prev) => (prev ? { ...prev, exiting: true } : null));
      exitRef.current = setTimeout(() => playNextRef.current(), TOAST_EXIT_MS);
    },
    [clearTimers],
  );

  useEffect(() => {
    return () => {
      clearTimers();
      queueRef.current = [];
    };
  }, [clearTimers]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast && (
        /* Faixa full-width + flex centrando a caixa. NÃO usar left-1/2 +
           -translate-x-1/2 aqui: elemento fixed encolhe pro espaço disponível
           a partir do `left` (metade da viewport no mobile) ANTES do
           translate — o toast virava uma coluna estreita e alta (QA 10/07). */
        <div
          role="status"
          aria-live="polite"
          className={[
            "pointer-events-none fixed inset-x-0 bottom-[calc(6rem+var(--focus-dock-h))] z-70 flex justify-center px-6",
            toast.exiting
              ? "animate-out fade-out slide-out-to-bottom-2 duration-200 [animation-fill-mode:forwards]"
              : "animate-in fade-in slide-in-from-bottom-4 duration-300",
          ].join(" ")}
        >
          <div
            className={[
              "pointer-events-auto flex max-w-[24rem] items-center gap-3 rounded-2xl border bg-surface/95 px-4 py-3 shadow-xl backdrop-blur-md",
              // Sucesso usa o dourado da marca (primary) — o verde cru
              // destoava da paleta quente do app.
              toast.type === "success"
                ? "border-primary/25"
                : toast.type === "error"
                  ? "border-red-400/25"
                  : "border-surface-alt",
            ].join(" ")}
          >
            {toast.type === "success" && (
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15">
                <CheckCircle2 className="h-4 w-4 text-primary" />
              </span>
            )}
            {toast.type === "error" && (
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-400/10">
                <XCircle className="h-4 w-4 text-red-400" />
              </span>
            )}
            <p className="font-body text-[13px] leading-relaxed text-text-main">
              {toast.message}
            </p>
            {toast.action && (
              <button
                type="button"
                onClick={() => toast.action && pressAction(toast.action)}
                className="-my-1 -mr-1 shrink-0 rounded-xl px-2.5 py-1.5 font-body text-[13px] font-bold text-accent transition-colors hover:bg-surface-alt"
              >
                {toast.action.label}
              </button>
            )}
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastCtx {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast deve ser usado dentro de ToastProvider");
  return ctx;
}

/**
 * O toast, quando ele é um BÔNUS e não um requisito.
 *
 * `useToast` lança sem provider, e está certo: quem depende de avisar o usuário
 * precisa saber na hora que o aviso não vai sair. Mas há um caso em que o throw
 * é a resposta errada — o **card de erro da vitrine nativa**, que existe
 * justamente para quando a tela já falhou. Ali um `useToast` cru transformaria
 * a ausência do provider em tela branca: o botão de diagnóstico derrubando a
 * superfície sendo diagnosticada.
 *
 * ⚠️ Só use isto onde houver uma SEGUNDA confirmação visível (o rótulo do
 * próprio botão, um estado na tela). Sem ela, degradar para `null` produz um
 * controle que não dá sinal de nada — a classe do BUG-58.
 */
export function useOptionalToast(): ToastCtx | null {
  return useContext(ToastContext);
}
